// src/app/pages/tool/session-manager/session-manager.component.ts

import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SessionPersistenceService, SessionMetadata, CheckpointStage } from './session-persistence.service';
import { SpinnerService } from '../../../shared/services/spinner.service';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import { StatoSessioneService } from '../../../shared/services/stato-sessione.service';
import { ApiService } from '../../../shared/services/api.service';
import { ProgettiService } from '../../../shared/services/progetti.service';


@Component({
  selector: 'app-session-manager',
  templateUrl: './session-manager.component.html',
  styleUrls: ['./session-manager.component.scss']
})
export class SessionManagerComponent implements OnInit, OnDestroy {
  projectId: string;
  sessions: SessionMetadata[] = [];
  loading = true;
  private destroy$ = new Subject<void>();

  templates: any[] = [];
  activeUploadMethod: 'story' | 'workspace' | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private sessionService: SessionPersistenceService,
    private sessioneService: StatoSessioneService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private apiService: ApiService,
    private progettiService: ProgettiService
  ) { }

  ngOnInit(): void {
    this.sessioneService.resetAll();
    this.sessionService.resetSessionTracking();

    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.projectId = params['project_id'];
        this.sessioneService.updateProjectId(this.projectId, true);

        // **** 3. CALL the new initialization method ****
        this.initializeProjectData(this.projectId);
        this.loadUserSessions();
      });

    this.sessioneService.templates$.pipe(takeUntil(this.destroy$)).subscribe(templates => {
      this.templates = templates;
    });
  }

  private initializeProjectData(projectId: string): void {
    // Use combineLatest to fetch project language and templates in parallel
    combineLatest([
      this.progettiService.getProjectLanguage(projectId),
      this.apiService.getTemplates(projectId)
    ]).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: ([language, templates]) => {
        if (language) {
          this.sessioneService.updateProjectLanguage(language);
          // console.log('Se mi leggi due volte qui va un take (1) Project language set:', language);
        }
        if (templates) {
          this.sessioneService.updateTemplates(templates);
          // console.log('Project templates set.');
        }
      },
      error: (error) => {
        console.error('Error fetching project initialization data:', error);
        this.toastr.error('Failed to load project configuration.');
      }
    });
  }

  navigateToConfiguration(projectId: string) {
    event?.stopPropagation();
    this.router.navigate(['/projects/configuration', projectId]);
  }

  navigateToKnowledgeBase(projectId: string) {
    event?.stopPropagation();
    this.router.navigate(['/projects/knowledge', projectId]);
  }

  loadUserSessions(): void {
    this.loading = true;
    this.spinnerService.show();

    // No need to pass parameters - the service gets them from observables
    this.sessionService.getUserSessions()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (sessions) => {
          this.sessions = sessions;
          this.loading = false;
          this.spinnerService.hide();
        },
        error: (error) => {
          console.error('Error loading sessions:', error);
          this.loading = false;
          this.spinnerService.hide();
          this.toastr.error('Failed to load sessions');
        }
      });
  }

  handleUploadMethodChange(method: 'story' | 'workspace' | null): void {
    this.activeUploadMethod = method;

    // We just need to update the local state
    // Navigation is now fully handled in the TestFileUploadComponent
    // console.log(`Upload method changed to: ${method}`);
  }

  onTemplateSelected(templateName: string): void {
    // This is called when the test-file-upload component selects a template
    // We don't need to do anything here since the component already updates the session service
    // console.log('Template selected:', templateName);
  }

  continueWithoutSession(): void {
    // Reset session tracking to ensure we don't update an existing session
    this.sessionService.resetSessionTracking();

    this.sessioneService.resetTokenUsage();

    // Navigate to the story component without loading a session
    this.router.navigate(['/projects/story', this.projectId]);
  }

  // Updated to use soft delete (hide) instead of hard delete
  deleteSession(sessionId: string, event: Event): void {
    event.stopPropagation(); // Prevent row click from triggering

    Swal.fire({
      title: 'Hide Session',
      text: 'Are you sure you want to hide this session? You won\'t see it in your list anymore.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, hide it'
    }).then((result) => {
      if (result.isConfirmed) {
        this.spinnerService.show();

        // Use hideSession instead of deleteSession
        this.sessionService.hideSession(sessionId).subscribe({
          next: () => {
            this.loadUserSessions();
            this.toastr.success('Session hidden successfully');
          },
          error: (error) => {
            console.error('Error hiding session:', error);
            this.spinnerService.hide();
            this.toastr.error('Failed to hide session');
          }
        });
      }
    });
  }

  loadSession(sessionId: string): void {
    this.spinnerService.show();

    // console.log('DEBUG: About to load session with ID:', sessionId);

    this.sessionService.loadSession(sessionId).subscribe({
      next: (session) => {
        // console.log('DEBUG: Session loaded successfully:', session);

        this.spinnerService.hide();
        this.toastr.success('Session loaded successfully');

        // Navigate to the appropriate view.
        this.router.navigate([ '/projects/story', this.projectId, 'session', session.sessionId ]);
      },
      error: (error) => {
        console.error('Error loading session:', error);
        this.spinnerService.hide();
        this.toastr.error('Failed to load session');
      }
    });
  }

  getFormattedDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString();
  }

  // Helper method to get a user-friendly name for checkpoint stages
  getCheckpointStageName(stage: CheckpointStage): string {
    switch (stage) {
      case 'scenarios': return 'Scenarios';
      case 'acs': return 'Analysis & Criteria';
      case 'tests': return 'Generated Tests';
      default: return 'Unknown';
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handles sharing a new session or extending an existing share.
   * Calls the backend to generate/update share details and updates the UI.
   * @param session The session metadata object from the list.
   * @param event The mouse event to stop propagation.
   */
  shareSession(session: SessionMetadata, event: Event): void {
    event.stopPropagation();
    this.spinnerService.show();

    // La risposta dal backend ora contiene anche 'expiresAt'
    this.sessionService.enableSharing(session.sessionId).subscribe({
      next: (response: { shareId: string, expiresAt: string }) => {
        const wasAlreadyShared = session.isShared;

        // Aggiorna lo stato locale dell'oggetto sessione per riflettere immediatamente il cambiamento nella UI
        session.isShared = true;
        session.shareId = response.shareId;
        session.shareExpiresAt = response.expiresAt; // Aggiorna la data di scadenza

        this.spinnerService.hide();

        // Se la sessione era già condivisa, questa era un'azione di "rinnovo"
        if (wasAlreadyShared) {
          this.toastr.success('Sharing period has been extended for 30 days!');
        } else {
          // Se era una nuova condivisione, mostra il popup con il link
          const shareUrl = `${window.location.origin}/shared-session/${response.shareId}`;

          Swal.fire({
            title: 'Session Shared!',
            html: `Anyone with this link can view a copy of your session.<br><br>
                   <b>The link will expire in 30 days.</b><br><br>
                   <input type="text" value="${shareUrl}" class="form-control" readonly>`,
            icon: 'success',
            confirmButtonText: 'Copy Link & Close',
            showCancelButton: true,
            cancelButtonText: 'Close',
            preConfirm: () => {
              // Copia il link negli appunti quando l'utente clicca il pulsante di conferma
              navigator.clipboard.writeText(shareUrl).then(() => {
                this.toastr.success('Link copied to clipboard!');
              });
              return true;
            }
          });
        }
      },
      error: (err) => {
        // Gestione robusta degli errori in caso di fallimento della chiamata API
        this.spinnerService.hide();
        this.toastr.error('Failed to share the session. Please try again.', 'Error');
        console.error('Error enabling session sharing:', err);
      }
    });
  }

  // NUOVO METODO per disabilitare la condivisione
  unshareSession(session: SessionMetadata, event: Event): void {
    event.stopPropagation();

    Swal.fire({
      title: 'Stop Sharing?',
      text: 'The public link will no longer work. Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, stop sharing',
      confirmButtonColor: '#d33',
    }).then(result => {
      if (result.isConfirmed) {
        this.spinnerService.show();
        this.sessionService.disableSharing(session.sessionId).subscribe({
          next: () => {
            session.isShared = false;
            session.shareId = undefined;
            this.spinnerService.hide();
            this.toastr.success('Session sharing has been disabled.');
          },
          error: (err) => {
            this.spinnerService.hide();
            this.toastr.error('Failed to disable sharing.');
            console.error(err);
          }
        });
      }
    });
  }

  // NUOVO METODO per copiare il link
  copyShareLink(shareId: string, event: Event): void {
    event.stopPropagation();
    const shareUrl = `${window.location.origin}/shared-session/${shareId}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      this.toastr.success('Share link copied to clipboard!');
    }).catch(err => {
      this.toastr.error('Could not copy link.');
      console.error('Copy to clipboard failed:', err);
    });
  }
}
